import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { CASE_STUDIES } from "../data/caseStudies";

export default function HubPage() {
  return (
    <div data-testid="hub-page" className="min-h-screen bg-[#050505] text-white">
      <Navbar />

      {/* ========= HERO ========= */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-[#9D00FF]/20 blur-[120px]" />
          <div className="absolute top-20 right-0 w-[500px] h-[500px] rounded-full bg-[#FF1493]/15 blur-[140px]" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-2 mb-8"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 px-3 h-7 text-[11px] uppercase tracking-[0.22em] text-white/80">
              <Sparkles className="w-3 h-3 text-[#FF1493]" />
              Études de cas
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif-display text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-[-0.03em] max-w-5xl"
          >
            L'influence, <em className="italic font-light text-white/70">mesurée</em>.
            <br />
            La performance, <span className="ls-gradient-text">racontée</span>.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-8 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed"
          >
            Plongée chiffrée dans les campagnes signées LinkSync : dispositifs, résultats, apprentissages. Le récit complet derrière les KPIs.
          </motion.p>
        </div>
      </section>

      {/* ========= CASE STUDIES LIST ========= */}
      <section className="relative">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24">
          <div className="flex items-end justify-between mb-10 border-b border-white/10 pb-6">
            <div>
              <div className="ls-overline text-[#FF1493] mb-2">Index</div>
              <h2 className="font-serif-display text-3xl md:text-4xl tracking-tight">
                Les études de cas
              </h2>
            </div>
            <div className="hidden md:block text-xs text-white/50 uppercase tracking-[0.22em]">
              Cliquez pour lire
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {CASE_STUDIES.map((cs, i) => (
              <CaseCard key={cs.slug} cs={cs} index={i} />
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function CaseCard({ cs, index }) {
  const isFeatured = true; // all cards full-width editorial stacked
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      className="md:col-span-12 group"
    >
      <Link
        to={`/etudes-de-cas/${cs.slug}`}
        data-testid={`case-card-${cs.slug}`}
        className="block relative rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br from-[#0f0f12] to-[#050505] hover:border-[#FF1493]/40 transition"
      >
        <div className={`grid ${isFeatured ? "md:grid-cols-12" : "grid-cols-1"} gap-0`}>
          <div className={`${isFeatured ? "md:col-span-7" : ""} p-8 md:p-12`}>
            <div className="flex items-center justify-between mb-8">
              <span className="ls-overline text-[#FF1493]">
                Étude signée LinkSync
              </span>
              <span className="ls-overline">{cs.client_type}</span>
            </div>

            <h3 className="font-serif-display text-3xl md:text-[44px] leading-[1.05] tracking-[-0.02em] mb-5 group-hover:text-white/90">
              {cs.title}
            </h3>

            <p className="text-white/60 text-base md:text-lg max-w-2xl leading-relaxed">
              {cs.subtitle}
            </p>

            <div className="flex flex-wrap items-center gap-3 mt-8">
              {cs.tags.map((t) => (
                <span
                  key={t}
                  className="text-[11px] uppercase tracking-[0.18em] rounded-full border border-white/15 px-3 h-6 inline-flex items-center text-white/70"
                >
                  {t}
                </span>
              ))}
            </div>

            <div className="mt-10 flex items-center gap-2 text-sm text-white">
              <span className="ls-link">Lire l'étude de cas</span>
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
            </div>
          </div>

          {isFeatured && (
            <div className="md:col-span-5 relative min-h-[320px] md:min-h-0 overflow-hidden bg-[#0a0a0a]">
              <img
                src={cs.cover_image}
                alt=""
                className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-l from-transparent via-black/60 to-black/85" />
              <div className="relative z-10 h-full p-8 md:p-12 flex flex-col justify-between">
                <div className="ls-overline text-white/80">Étude complète</div>
                <div>
                  <div className="font-serif-display text-5xl md:text-[72px] leading-none tracking-tighter text-white/90">
                    Lire →
                  </div>
                  <div className="mt-6 text-sm text-white/60">
                    {cs.channels.join(" · ")}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Link>
    </motion.div>
  );
}
